interface NodeWithType {
  id: string;
  type: string;
}

interface ConstraintConfig {
  acceptedChildNodes?: string[];
  acceptedParentNodes?: string[];
}

/**
 * - it is not connecting to itself
 * - a connection is valid if it does not violate the constraints of the source or target node
 * - it is not forming a cycle
 */
export const isValidConnection =
  (
    nodes: NodeWithType[],
    _edges: unknown[],
    Constraints: Record<string, ConstraintConfig> | null,
    showSnackbar: (message: string, severity?: 'error' | 'success' | 'warning' | 'info') => void
  ) =>
  (params: { source: string; target: string }) => {
    const { source, target } = params;

    // 1. self-connection check
    if (source === target) {
      showSnackbar('Cannot connect to itself', 'error');
      return false;
    }

    // 2. constraints check
    const sourceNode = nodes.find((node) => node.id === source);
    const targetNode = nodes.find((node) => node.id === target);

    // check acceptedChildNodes constraints of the source node
    const sourceConstraints = Constraints?.[sourceNode.type];

    if (sourceConstraints && sourceConstraints.acceptedChildNodes.length > 0) {
      const isValid = sourceConstraints.acceptedChildNodes.includes(targetNode.type);
      if (!isValid) {
        showSnackbar(
          `${sourceNode.type} cannot connect to ${targetNode.type} due to child node restrictions`,
          'error'
        );
        return false;
      }
    }

    // check acceptedParentNodes constraints of the target node
    const targetConstraints = Constraints?.[targetNode.type];

    if (targetConstraints && targetConstraints.acceptedParentNodes.length > 0) {
      const isValid = targetConstraints.acceptedParentNodes.includes(sourceNode.type);
      if (!isValid) {
        showSnackbar(
          `${targetNode.type} cannot accept connection from ${sourceNode.type} due to parent node restrictions`,
          'error'
        );
        return false;
      }
    }

    return true;
  };
